if (!customElements.get("subscribe-save-selector")) {
  customElements.define(
    "subscribe-save-selector",
    class SubscribeSaveSelector extends HTMLElement {
      connectedCallback() {
        this.bindControls();

        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data?.html || data.sectionId !== this.dataset.sectionId) return;
            const updated = data.html.getElementById(this.id);
            if (!updated) {
              this.hidden = true;
              this.syncTierContainer(data.html);
              this.setSellingPlan("");
              return;
            }

            const selectedMode = this.querySelector("[data-subscribe-save-mode]:checked")?.value;
            const selectedPlanId = this.planSelect?.value;
            const selectedQuantity = this.selectedQuantity;

            this.hidden = false;
            this.syncRootAttributes(updated);
            this.syncTierContainer(data.html);
            this.innerHTML = updated.innerHTML;
            this.bindControls();
            this.restoreSelection(selectedMode, selectedPlanId, selectedQuantity);
            this.syncSellingPlan();
          });
        }

        this.syncSellingPlan();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      bindControls() {
        this.modeInputs = Array.from(this.querySelectorAll("[data-subscribe-save-mode]"));
        this.tierContainer = document.getElementById(`SubscribeSaveTiers-${this.dataset.sectionId}`);
        this.tierInputs = Array.from(this.tierContainer?.querySelectorAll("[data-subscribe-save-tier]") || []);
        this.planSelect = this.querySelector("[data-subscribe-save-plan-select]");
        this.sellingPlanInput = this.querySelector("[data-subscribe-save-selling-plan-input]");

        this.modeInputs.forEach((input) => {
          input.addEventListener(
            "change",
            this.syncSellingPlanBound || (this.syncSellingPlanBound = this.syncSellingPlan.bind(this)),
          );
        });

        this.tierInputs.forEach((input) => {
          input.addEventListener(
            "change",
            this.syncSellingPlanBound || (this.syncSellingPlanBound = this.syncSellingPlan.bind(this)),
          );
        });

        this.planSelect?.addEventListener(
          "change",
          this.syncSellingPlanBound || (this.syncSellingPlanBound = this.syncSellingPlan.bind(this)),
        );

        this.decorateBenefits();
      }

      syncRootAttributes(updated) {
        [
          "data-money-format",
          "data-variant-price",
          "data-period-days",
          "data-servings",
          "data-one-time-price",
          "data-one-time-compare-price",
          "data-cart-total-items-template",
        ].forEach((attribute) => {
          const value = updated.getAttribute(attribute);
          if (value === null) {
            this.removeAttribute(attribute);
          } else {
            this.setAttribute(attribute, value);
          }
        });
      }

      syncTierContainer(html) {
        const currentTiers = document.getElementById(`SubscribeSaveTiers-${this.dataset.sectionId}`);
        const updatedTiers = html?.getElementById(`SubscribeSaveTiers-${this.dataset.sectionId}`);
        if (!currentTiers) return;

        if (updatedTiers) {
          currentTiers.innerHTML = updatedTiers.innerHTML;
          currentTiers.hidden = updatedTiers.hidden;
        } else {
          currentTiers.innerHTML = "";
          currentTiers.hidden = true;
        }
      }

      restoreSelection(selectedMode, selectedPlanId, selectedQuantity) {
        if (selectedMode) {
          const modeInput = this.querySelector(`[data-subscribe-save-mode][value="${selectedMode}"]`);
          if (modeInput && !modeInput.disabled) modeInput.checked = true;
        }

        if (selectedMode === "subscription" && selectedPlanId && this.planSelect) {
          const matchingOption = Array.from(this.planSelect.options).find((option) => option.value === selectedPlanId);
          if (matchingOption) this.planSelect.value = selectedPlanId;
        }

        if (selectedQuantity) {
          const matchingTier = this.tierContainer?.querySelector(
            `[data-subscribe-save-tier][data-quantity="${selectedQuantity}"]`,
          );
          if (matchingTier) matchingTier.checked = true;
        }
      }

      syncSellingPlan() {
        const selectedMode = this.querySelector("[data-subscribe-save-mode]:checked")?.value;
        const sellingPlanId = selectedMode === "subscription" ? this.planSelect?.value : "";
        this.updateSelectedState();
        this.syncQuantity();
        this.syncLineProperties();
        this.setSellingPlan(sellingPlanId);
        this.updateTierPrices(selectedMode);
        this.updatePurchaseOptionPrices(selectedMode);
        this.updateSubmitPrice(selectedMode);
        this.updateTierImage();
      }

      updateSelectedState() {
        this.querySelectorAll(".xts-subscribe-save__option").forEach((option) => {
          option.classList.toggle("is-selected", Boolean(option.querySelector("[data-subscribe-save-mode]:checked")));
        });

        this.tierContainer?.querySelectorAll(".xts-subscribe-save__tier").forEach((tier) => {
          tier.classList.toggle("is-selected", Boolean(tier.querySelector("[data-subscribe-save-tier]:checked")));
        });
      }

      decorateBenefits() {
        const iconTemplate = this.querySelector("[data-subscribe-save-benefit-icon]");
        if (!iconTemplate) return;

        this.querySelectorAll(".xts-subscribe-save__benefits li").forEach((item) => {
          if (item.querySelector(".xts-subscribe-save__benefit-icon")) return;

          const content = document.createElement("span");
          content.className = "xts-subscribe-save__benefit-content";
          const title = document.createElement("span");
          title.className = "xts-subscribe-save__benefit-title";
          const description = document.createElement("span");
          description.className = "xts-subscribe-save__benefit-description";
          const lineBreak = Array.from(item.childNodes).find((node) => node.nodeName === "BR");

          if (lineBreak) {
            while (item.firstChild && item.firstChild !== lineBreak) {
              title.append(item.firstChild);
            }
            lineBreak.remove();
          }

          while (item.firstChild) {
            description.append(item.firstChild);
          }

          if (title.textContent.trim() || title.children.length) content.append(title);
          if (description.textContent.trim() || description.children.length) content.append(description);
          item.append(iconTemplate.content.firstElementChild.cloneNode(true), content);
        });
      }

      setSellingPlan(sellingPlanId) {
        const input = this.sellingPlanInput || this.querySelector("[data-subscribe-save-selling-plan-input]");
        if (!input) return;

        input.value = sellingPlanId || "";
        input.disabled = !sellingPlanId;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }

      syncQuantity() {
        const productForm = document.getElementById(this.dataset.productFormId);
        if (!productForm) return;

        const quantity = this.selectedQuantity;
        let quantityInput =
          document.querySelector(`[name="quantity"][form="${this.dataset.productFormId}"]`) ||
          productForm.querySelector('[name="quantity"]');

        if (!quantityInput) {
          quantityInput = document.createElement("input");
          quantityInput.type = "hidden";
          quantityInput.name = "quantity";
          productForm.append(quantityInput);
        }

        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      syncLineProperties() {
        const productForm = document.getElementById(this.dataset.productFormId);
        if (!productForm) return;

        const properties = {
          _xts_bundle_badge: this.selectedTier?.dataset.badge || "",
          _xts_bundle_quantity: this.selectedQuantity,
          _xts_cart_total_items_text: this.dataset.cartTotalItemsTemplate || "You will get: [quantity] [item] in total",
          _xts_period_days: this.parseNumber(this.dataset.periodDays, 0),
          _xts_servings: this.parseNumber(this.dataset.servings, 1),
        };

        Object.entries(properties).forEach(([key, value]) => {
          let input = productForm.querySelector(`[name="properties[${key}]"]`);
          if (!input) {
            input = document.createElement("input");
            input.type = "hidden";
            input.name = `properties[${key}]`;
            productForm.append(input);
          }
          input.value = value;
        });
      }

      updateTierPrices(selectedMode) {
        this.tierInputs.forEach((input) => {
          const price = this.calculateTotalPrice({
            selectedMode,
            quantity: this.parseNumber(input.dataset.quantity, 1),
            quantityDiscountPercent: this.parseNumber(input.dataset.discountPercent, 0),
          });
          const tier = input.closest(".xts-subscribe-save__tier");
          const priceElement = tier?.querySelector("[data-subscribe-save-tier-price]");
          if (priceElement) priceElement.textContent = this.formatMoney(price);

          const days = this.calculateSupplyDays(this.parseNumber(input.dataset.quantity, 1));
          const perDayElement = tier?.querySelector("[data-subscribe-save-tier-per-day]");
          const daysElement = tier?.querySelector("[data-subscribe-save-tier-days]");

          if (perDayElement) {
            perDayElement.textContent = days > 0 ? `${this.formatMoney(price / days)} per day` : "";
          }

          if (daysElement) {
            daysElement.textContent = days > 0 ? `${this.formatNumber(days)} days` : "";
          }
        });
      }

      updatePurchaseOptionPrices() {
        const oneTimePrice = this.querySelector("[data-subscribe-save-one-time-price]");
        const subscriptionPrice = this.querySelector("[data-subscribe-save-subscription-price]");
        const selectedPlan = this.planSelect?.selectedOptions?.[0];

        if (oneTimePrice) {
          const oneTimeTotal = this.calculateTotalPrice({
            selectedMode: "one_time",
            unitPrice: this.variantPrice,
            quantity: this.selectedQuantity,
            quantityDiscountPercent: this.selectedQuantityDiscountPercent,
          });
          const compareTotal = this.variantPrice * this.selectedQuantity;

          oneTimePrice.innerHTML = "";
          if (oneTimeTotal < compareTotal) {
            const sale = document.createElement("span");
            sale.className = "xts-subscribe-save__sale-price";
            sale.textContent = this.formatMoney(oneTimeTotal);

            const old = document.createElement("s");
            old.className = "xts-subscribe-save__old-price";
            old.textContent = this.formatMoney(compareTotal);

            oneTimePrice.append(sale, old);
          } else {
            const regular = document.createElement("span");
            regular.className = "xts-subscribe-save__regular-price";
            regular.textContent = this.formatMoney(oneTimeTotal);
            oneTimePrice.append(regular);
          }
        }

        if (!subscriptionPrice || !selectedPlan) return;

        const subscriptionTotal = this.calculateTotalPrice({
          selectedMode: "subscription",
          unitPrice: this.parseNumber(selectedPlan.dataset.sellingPlanPrice, this.variantPrice),
          quantity: this.selectedQuantity,
          quantityDiscountPercent: this.selectedQuantityDiscountPercent,
        });
        const compareTotal = this.variantPrice * this.selectedQuantity;

        subscriptionPrice.innerHTML = "";
        if (subscriptionTotal < compareTotal) {
          const sale = document.createElement("span");
          sale.className = "xts-subscribe-save__sale-price";
          sale.textContent = this.formatMoney(subscriptionTotal);

          const old = document.createElement("s");
          old.className = "xts-subscribe-save__old-price";
          old.textContent = this.formatMoney(compareTotal);

          subscriptionPrice.append(sale, old);
        } else {
          const regular = document.createElement("span");
          regular.className = "xts-subscribe-save__regular-price";
          regular.textContent = this.formatMoney(subscriptionTotal);
          subscriptionPrice.append(regular);
        }
      }

      updateSubmitPrice(selectedMode) {
        const productForm = document.getElementById(this.dataset.productFormId);
        const priceWrapper = productForm?.querySelector(".product-form__submit-prices");
        if (!priceWrapper) return;

        const selectedPlan = this.planSelect?.selectedOptions?.[0];
        const unitPrice =
          selectedMode === "subscription"
            ? this.parseNumber(selectedPlan?.dataset.sellingPlanPrice, this.variantPrice)
            : this.variantPrice;
        const totalPrice = this.calculateTotalPrice({
          selectedMode,
          unitPrice,
          quantity: this.selectedQuantity,
          quantityDiscountPercent: this.selectedQuantityDiscountPercent,
        });
        const compareTotal = this.variantPrice * this.selectedQuantity;
        const price = this.formatMoney(totalPrice);
        const comparePrice = totalPrice < compareTotal ? this.formatMoney(compareTotal) : "";

        const priceElement = priceWrapper.querySelector(".product-form__submit-price");
        if (priceElement && price) priceElement.textContent = price;

        let comparePriceElement = priceWrapper.querySelector(".product-form__submit-compare-price");
        if (comparePrice) {
          if (!comparePriceElement) {
            comparePriceElement = document.createElement("s");
            comparePriceElement.className = "product-form__submit-compare-price";
            priceWrapper.append(comparePriceElement);
          }
          comparePriceElement.textContent = comparePrice;
        } else {
          comparePriceElement?.remove();
        }
      }

      updateTierImage() {
        const tier = this.selectedTier;
        if (!tier?.dataset.imageSrc) return;

        const gallery = document.getElementById(`MediaGallery-${this.dataset.sectionId}`);
        const activeMedia = gallery?.querySelector(".product__media-item.is-active") || gallery?.querySelector(".product__media-item");
        const activeImage = activeMedia?.querySelector("img");
        if (!activeImage) return;

        activeImage.src = tier.dataset.imageSrc;
        if (tier.dataset.imageSrcset) activeImage.srcset = tier.dataset.imageSrcset.trim();
        if (tier.dataset.imageWidth) activeImage.width = this.parseNumber(tier.dataset.imageWidth, activeImage.width);
        if (tier.dataset.imageHeight) activeImage.height = this.parseNumber(tier.dataset.imageHeight, activeImage.height);
        activeImage.alt = tier.dataset.imageAlt || activeImage.alt;

        const activeMediaId = activeMedia.dataset.mediaId;
        const thumbnailImage = activeMediaId
          ? gallery?.querySelector(`[data-target="${activeMediaId}"] img`)
          : null;

        if (!thumbnailImage) return;
        thumbnailImage.src = tier.dataset.imageSrc;
        if (tier.dataset.imageSrcset) thumbnailImage.srcset = tier.dataset.imageSrcset.trim();
        thumbnailImage.alt = tier.dataset.imageAlt || thumbnailImage.alt;
      }

      calculateTotalPrice({ selectedMode, unitPrice, quantity, quantityDiscountPercent }) {
        const price = unitPrice ?? (selectedMode === "subscription" ? this.selectedSellingPlanPrice : this.variantPrice);
        const discountAmount = Math.floor(price * (quantityDiscountPercent / 100));
        const discountedUnitPrice = price - discountAmount;
        return discountedUnitPrice * quantity;
      }

      calculateSupplyDays(quantity) {
        const periodDays = this.parseNumber(this.dataset.periodDays, 30);
        if (periodDays <= 0 || quantity <= 0) return 0;

        return periodDays * quantity;
      }

      formatMoney(cents) {
        const value = Math.round(this.parseNumber(cents, 0));
        const format = this.dataset.moneyFormat || "${{amount}}";
        const amount = (value / 100).toFixed(2);
        const amountNoDecimals = Math.round(value / 100).toString();
        const amountWithComma = amount.replace(".", ",");

        return format
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
          .replace(/\{\{\s*amount\s*\}\}/g, amount);
      }

      parseNumber(value, fallback = 0) {
        const number = Number(value);
        return Number.isFinite(number) ? number : fallback;
      }

      formatNumber(value) {
        return Number.isInteger(value) ? value.toString() : value.toFixed(1).replace(/\.0$/, "");
      }

      get variantPrice() {
        return this.parseNumber(this.dataset.variantPrice, 0);
      }

      get selectedTier() {
        return this.tierContainer?.querySelector("[data-subscribe-save-tier]:checked") || this.tierInputs?.[0];
      }

      get selectedQuantity() {
        return Math.max(1, this.parseNumber(this.selectedTier?.dataset.quantity, 1));
      }

      get selectedQuantityDiscountPercent() {
        return Math.max(0, this.parseNumber(this.selectedTier?.dataset.discountPercent, 0));
      }

      get selectedSellingPlanPrice() {
        return this.parseNumber(this.planSelect?.selectedOptions?.[0]?.dataset.sellingPlanPrice, this.variantPrice);
      }
    },
  );
}
